import classNames from 'classnames';
import type { FC, ReactNode } from 'react';
import { useState } from 'react';

export interface ChipProps {
  id: string;
  label?: string;
  icon: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  hoverEffect?: boolean;
  shadowEffect?: boolean;
  onClick?: (id: string) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const Chip: FC<ChipProps> = ({
  id,
  label,
  icon,
  selected = false,
  disabled = false,
  hoverEffect = true,
  shadowEffect = true,
  onClick,
  size = 'lg',
  className,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const sizeClasses = {
    sm: 'w-10 h-10', // 40x40px
    md: 'w-14 h-14', // 56x56px
    lg: 'w-16 h-16', // 64x64px
  };

  const iconSizes = {
    sm: 'w-6 h-6', // 24x24px
    md: 'w-9 h-9', // 36x36px
    lg: 'w-10 h-10', // 40x40px
  };

  // Determine which shadow to apply based on state priority
  const getShadowClass = () => {
    if (disabled) return 'shadow-chipDefault';
    if (selected) return 'shadow-chipSelected';
    if (hoverEffect && isHovered) return 'shadow-chipHover';
    if (!shadowEffect) return '';
    return 'shadow-chipDefault';
  };

  return (
    <button
      type="button"
      disabled={disabled}
      className={classNames(
        sizeClasses[size],
        'rounded-lg border-0 flex items-center justify-center transition-all duration-200 backdrop-blur-md',
        getShadowClass(),
        {
          'opacity-50 cursor-not-allowed': disabled,
          'bg-bank-100': selected,
          'cursor-default': !hoverEffect,
        },
        className,
      )}
      onMouseEnter={
        hoverEffect ? () => !disabled && setIsHovered(true) : undefined
      }
      onMouseLeave={hoverEffect ? () => setIsHovered(false) : undefined}
      onClick={() => !disabled && onClick?.(id)}
      title={label}
      aria-label={label}
      aria-pressed={selected}
    >
      <div
        className={classNames(
          iconSizes[size],
          'flex items-center justify-center',
          {
            'text-bank-600': selected,
            'text-primary-600': !selected && !disabled,
            'text-primary-400': disabled,
          },
        )}
      >
        {icon}
      </div>
    </button>
  );
};

export default Chip;
